import { Footprints, Wifi, Plus, AlarmClock, Settings, MoonStar } from "lucide-react";

const StatusBar = ({ time = "11:03" }) => (
  <div className="flex items-center justify-between px-6 pt-4">
    <span className="text-xs font-semibold text-ink">{time}</span>
    <span className="absolute left-1/2 top-3 h-[22px] w-[86px] -translate-x-1/2 rounded-full bg-[#111]" />
    <span className="flex items-center gap-1.5">
      <Wifi size={13} className="text-ink" />
      <span className="block h-[10px] w-[20px] rounded-[3px] border border-ink/60 p-[1px]">
        <span className="block h-full w-3/4 rounded-[2px] bg-ink" />
      </span>
    </span>
  </div>
);

const Toggle = ({ on }) => (
  <span
    className={`flex h-7 w-12 items-center rounded-full p-1 transition-colors ${
      on ? "justify-end bg-terra" : "justify-start bg-line"
    }`}
  >
    <span className="h-5 w-5 rounded-full bg-white shadow" />
  </span>
);

const MissionScreen = () => (
  <div className="flex h-full flex-col bg-cream px-5 pb-6">
    <StatusBar />
    <div className="mt-8 flex flex-col items-center text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-terrasoft">
        <Footprints size={26} className="text-terra" />
      </span>
      <p className="mt-5 font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-mutedwarm">
        Alarm mission
      </p>
      <p className="mt-3 font-serif text-3xl text-ink">Wake up.</p>
      <p className="font-serif text-3xl italic text-terra">Walk.</p>
      <p className="mt-2 text-xs text-mutedwarm">30 steps remaining</p>
    </div>
    <div className="mt-7 rounded-2xl border border-line bg-white/70 p-5 text-center shadow-sm">
      <p className="font-serif text-5xl font-light text-ink">30</p>
      <p className="mt-1 text-[11px] text-mutedwarm">steps remaining</p>
      <div className="mt-4 h-1 w-full rounded-full bg-sand">
        <div className="h-1 w-[4%] rounded-full bg-terra" />
      </div>
      <p className="mt-2 text-[10px] text-mutedwarm">0% complete</p>
    </div>
    <div className="mt-auto flex justify-center pt-5">
      <span className="flex items-center gap-1.5 rounded-full bg-espresso px-4 py-1.5 text-[11px] font-medium text-cream">
        <MoonStar size={12} /> 5m
      </span>
    </div>
  </div>
);

const AlarmsScreen = () => (
  <div className="flex h-full flex-col bg-cream px-5 pb-6">
    <StatusBar />
    <div className="mt-6 flex justify-end">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm">
        <Plus size={16} className="text-ink" />
      </span>
    </div>
    <div className="mt-3 space-y-3">
      {[
        { time: "7:30", label: "Weekday Alarm", meta: "Weekdays", steps: 30, on: true },
        { time: "6:15", label: "Gym Day", meta: "Tue Thu", steps: 50, on: true },
      ].map((a) => (
        <div
          key={a.label}
          className="flex items-center justify-between rounded-2xl border border-line bg-white/70 p-4 shadow-sm"
        >
          <div>
            <p className="font-serif text-2xl font-light text-ink">
              {a.time} <span className="text-base">AM</span>
            </p>
            <p className="mt-0.5 text-xs font-semibold text-ink">{a.label}</p>
            <p className="mt-0.5 flex items-center gap-1 text-[10px] text-mutedwarm">
              {a.meta} <Footprints size={10} /> {a.steps}
            </p>
          </div>
          <Toggle on={a.on} />
        </div>
      ))}
    </div>
    <div className="mt-auto flex justify-center pt-5">
      <span className="flex items-center gap-5 rounded-full bg-espresso px-5 py-2">
        <span className="flex items-center gap-1 rounded-full bg-cream px-2.5 py-1 text-[10px] font-semibold text-ink">
          <AlarmClock size={11} /> Alarms
        </span>
        <span className="flex items-center gap-1 text-[10px] font-medium text-cream/80">
          <Settings size={11} /> Settings
        </span>
      </span>
    </div>
  </div>
);

const StepsScreen = () => (
  <div className="flex h-full flex-col bg-cream px-5 pb-6">
    <StatusBar time="11:01" />
    <div className="mt-8 text-center">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-mutedwarm">
        How it works
      </p>
      <p className="mt-3 font-serif text-2xl leading-snug text-ink">
        Three steps to a<br />better morning
      </p>
    </div>
    <div className="mt-6 space-y-0">
      {[
        { n: "01", t: "Set your alarm", d: "Choose a time and how many steps you need to take." },
        { n: "02", t: "Get up and walk", d: "When it fires, the only way to stop it is to start walking." },
        { n: "03", t: "Hit your goal", d: "Complete your steps and the alarm stops. No shortcuts." },
      ].map((s, i) => (
        <div key={s.n} className={`flex gap-3 py-3 ${i < 2 ? "border-b border-line" : ""}`}>
          <span className="font-serif text-sm italic text-terra">{s.n}</span>
          <div>
            <p className="text-xs font-bold text-ink">{s.t}</p>
            <p className="mt-0.5 text-[10px] leading-relaxed text-mutedwarm">{s.d}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-auto pt-4">
      <div className="mb-3 flex justify-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-line" />
        <span className="h-1.5 w-4 rounded-full bg-terra" />
        <span className="h-1.5 w-1.5 rounded-full bg-line" />
      </div>
      <div className="rounded-full bg-espresso py-2.5 text-center text-xs font-semibold text-cream">
        Continue
      </div>
      <p className="mt-2 text-center text-[10px] text-mutedwarm">Skip</p>
    </div>
  </div>
);

export const PhoneMockup = ({ screen = "mission", className = "" }) => (
  <div
    data-testid={`phone-mockup-${screen}`}
    className={`relative w-[280px] rounded-[44px] bg-[#141110] p-[10px] shadow-[0_40px_80px_-30px_rgba(43,36,32,0.45)] sm:w-[300px] ${className}`}
  >
    <div className="relative aspect-[9/19] overflow-hidden rounded-[36px]">
      {screen === "mission" && <MissionScreen />}
      {screen === "alarms" && <AlarmsScreen />}
      {screen === "steps" && <StepsScreen />}
    </div>
  </div>
);
